/**
 * RAG Chat Component
 * Query documents using semantic or hybrid search
 */
import { useState, useRef, useEffect } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import type { DocumentQueryResult } from '../../types';

export function RAGChat() {
  const [query, setQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [topK, setTopK] = useState(5);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Search mode configuration
  const [searchMode, setSearchMode] = useState<'semantic' | 'hybrid'>('hybrid');
  const [fusionMethod, setFusionMethod] = useState<'rrf' | 'weighted' | 'max'>('rrf');
  const [enableReRanking, setEnableReRanking] = useState(true);
  const [reRankAlgorithm, setReRankAlgorithm] = useState<'mmr' | 'relevance' | 'metadata' | 'recency' | 'combined'>('mmr');
  const [enableExpansion, setEnableExpansion] = useState(true);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { documents, queryDocuments, hybridQueryDocuments, queryResults, isQuerying, clearQueryResults } = useDocumentStore();

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [query]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || isQuerying) return;

    try {
      if (searchMode === 'hybrid') {
        await hybridQueryDocuments(query.trim(), {
          topK,
          documentIds: selectedDocs.length > 0 ? selectedDocs : undefined,
          enableExpansion,
          enableReRanking,
          fusionMethod,
          reRankAlgorithm,
        });
      } else {
        await queryDocuments(
          query.trim(),
          topK,
          selectedDocs.length > 0 ? selectedDocs : undefined
        );
      }
    } catch (error) {
      console.error('Query failed:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e);
    }
  };

  const toggleDocSelection = (docId: string) => {
    setSelectedDocs((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const selectAllDocs = () => {
    setSelectedDocs(documents.map((doc) => doc.id));
  };

  const clearSelection = () => {
    setSelectedDocs([]);
  };

  const handleClearResults = () => {
    clearQueryResults();
    setQuery('');
  };

  return (
    <div className="bg-gray-800/50 rounded-lg p-6">
      <h2 className="text-xl font-semibold text-white mb-4">RAG Query</h2>

      {documents.length === 0 ? (
        <div className="text-center py-8 bg-gray-900 rounded-lg border border-gray-700">
          <svg
            className="w-10 h-10 mx-auto mb-3 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-gray-400">Upload documents first to start querying</p>
        </div>
      ) : (
        <>
          {/* Document Selection */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-300">
                Search in documents:
              </label>
              <div className="flex gap-2">
                <button
                  onClick={selectAllDocs}
                  className="text-xs text-purple-400 hover:text-purple-300"
                >
                  Select All
                </button>
                <span className="text-gray-600">|</span>
                <button
                  onClick={clearSelection}
                  className="text-xs text-purple-400 hover:text-purple-300"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => toggleDocSelection(doc.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    selectedDocs.includes(doc.id)
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {doc.name.length > 20 ? `${doc.name.substring(0, 20)}...` : doc.name}
                </button>
              ))}
            </div>
            {selectedDocs.length === 0 && (
              <p className="text-xs text-gray-500 mt-2">
                No documents selected - will search in all documents
              </p>
            )}
          </div>

          {/* Query Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isQuerying}
                placeholder="Ask a question about your documents..."
                className="w-full bg-gray-900 text-white rounded-lg p-4 resize-none border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 min-h-[80px]"
                rows={2}
              />
            </div>

            {/* Advanced Settings */}
            <div className="border border-gray-700 rounded-lg bg-gray-900/50">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm text-gray-300 hover:text-white transition-colors"
              >
                <span className="font-medium">Search Configuration</span>
                <svg
                  className={`w-5 h-5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showAdvanced && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-700 pt-4">
                  {/* Search Mode */}
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">
                      Search Mode
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setSearchMode('semantic')}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          searchMode === 'semantic'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Semantic Only
                      </button>
                      <button
                        type="button"
                        onClick={() => setSearchMode('hybrid')}
                        className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                          searchMode === 'hybrid'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Hybrid (Recommended)
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {searchMode === 'semantic'
                        ? 'Uses only vector embeddings for semantic understanding'
                        : 'Combines semantic and keyword search for better results'}
                    </p>
                  </div>

                  {/* Hybrid-specific options */}
                  {searchMode === 'hybrid' && (
                    <>
                      {/* Fusion Method */}
                      <div>
                        <label className="text-sm font-medium text-gray-300 mb-2 block">
                          Fusion Method
                        </label>
                        <select
                          value={fusionMethod}
                          onChange={(e) => setFusionMethod(e.target.value as 'rrf' | 'weighted' | 'max')}
                          className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="rrf">RRF (Reciprocal Rank Fusion)</option>
                          <option value="weighted">Weighted Score Fusion</option>
                          <option value="max">Max Score Fusion</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          How to combine semantic and keyword search results
                        </p>
                      </div>

                      {/* Query Expansion Toggle */}
                      <div>
                        <label className="flex items-center justify-between cursor-pointer">
                          <div>
                            <span className="text-sm font-medium text-gray-300">Query Expansion</span>
                            <p className="text-xs text-gray-500">
                              Add synonyms, acronyms, and corrections to improve results
                            </p>
                          </div>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={enableExpansion}
                              onChange={(e) => setEnableExpansion(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </div>
                        </label>
                      </div>

                      {/* Re-ranking Toggle */}
                      <div>
                        <label className="flex items-center justify-between cursor-pointer">
                          <div>
                            <span className="text-sm font-medium text-gray-300">Re-ranking</span>
                            <p className="text-xs text-gray-500">
                              Apply advanced algorithms to improve result quality
                            </p>
                          </div>
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={enableReRanking}
                              onChange={(e) => setEnableReRanking(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </div>
                        </label>
                      </div>

                      {/* Re-ranking Algorithm (only if enabled) */}
                      {enableReRanking && (
                        <div>
                          <label className="text-sm font-medium text-gray-300 mb-2 block">
                            Re-ranking Algorithm
                          </label>
                          <select
                            value={reRankAlgorithm}
                            onChange={(e) => setReRankAlgorithm(e.target.value as any)}
                            className="w-full bg-gray-800 text-white rounded-lg px-3 py-2 text-sm border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="mmr">MMR (Maximal Marginal Relevance)</option>
                            <option value="relevance">Relevance-based</option>
                            <option value="metadata">Metadata-based</option>
                            <option value="recency">Recency-based</option>
                            <option value="combined">Combined (All signals)</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {reRankAlgorithm === 'mmr' && 'Balances relevance and diversity'}
                            {reRankAlgorithm === 'relevance' && 'Focuses on query-document similarity'}
                            {reRankAlgorithm === 'metadata' && 'Boosts results based on content features'}
                            {reRankAlgorithm === 'recency' && 'Prioritizes more recent documents'}
                            {reRankAlgorithm === 'combined' && 'Uses all signals for best quality'}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              {/* Top K Slider */}
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-400">Results:</label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={topK}
                  onChange={(e) => setTopK(Number(e.target.value))}
                  className="w-24"
                />
                <span className="text-sm text-gray-300 w-6">{topK}</span>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!query.trim() || isQuerying}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isQuerying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Searching...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    Search
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Query Results */}
          {queryResults.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">
                  Results ({queryResults.length})
                </h3>
                <button
                  onClick={handleClearResults}
                  className="text-sm text-gray-400 hover:text-gray-300"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-3">
                {queryResults.map((result, index) => (
                  <QueryResultCard key={index} result={result} rank={index + 1} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface QueryResultCardProps {
  result: DocumentQueryResult;
  rank: number;
}

function QueryResultCard({ result, rank }: QueryResultCardProps) {
  const scorePercent = Math.round(result.score * 100);

  return (
    <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-medium">
            {rank}
          </span>
          <div>
            <h4 className="font-medium text-white text-sm">{result.documentName}</h4>
            <p className="text-xs text-gray-500">Chunk {result.chunkIndex + 1}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-12 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500"
              style={{ width: `${scorePercent}%` }}
            />
          </div>
          <span className="text-xs text-gray-400">{scorePercent}%</span>
        </div>
      </div>

      {/* Content */}
      <p className="text-sm text-gray-300 leading-relaxed line-clamp-4">
        {result.content}
      </p>
    </div>
  );
}
